<!--
# Frontend Vue | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadLiveScanner
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
# created 2026-03-13 by Codex
# last edited 2026-03-15 by Codex // localize scanner texts through the visitor lead translation helper
-->
<template>
  <div class="visitor-live-scanner">
    <qrcode-stream :camera="cameraMode" @decode="onDecode" @init="onInit" />
    <button
      type="button"
      class="btn btn-sm btn-light rounded-pill scanner-switch-button"
      @click="switchCamera"
    >
      <i class="bi bi-camera-video"></i>
      <span class="visually-hidden">{{ t('scan.switchCamera') }}</span>
    </button>
    <div v-if="loading" class="scanner-loader">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">{{ t('common.loading') }}</span>
      </div>
    </div>
    <div v-if="error" class="scanner-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader'
import { getVisitorLeadLocale, translateVisitorLead, translateVisitorLeadError } from '@/components/visitor-lead/i18n.js'

export default {
  name: 'VisitorLeadLiveScanner',
  components: { QrcodeStream },
  data() {
    return {
      loading: true,
      error: '',
      locale: getVisitorLeadLocale(),
      cameraMode: 'rear',
      cameraModes: ['rear', 'front', 'auto']
    }
  },
  created() {
    this.cameraMode = this.getInitialCameraMode()
    this.cameraModes = this.getAvailableCameraModes()
  },
  methods: {
    onDecode(decodedString) {
      this.$emit('decode', decodedString)
    },
    async onInit(promise) {
      this.loading = true
      this.error = ''
      try {
        await promise
        this.$emit('ready')
      } catch (error) {
        const mappedError = this.mapError(error)
        this.error = mappedError.message
        this.$emit('error', { originalError: error, code: mappedError.code, message: mappedError.message, fallbackMessage: mappedError.fallbackMessage })
      } finally {
        this.loading = false
      }
    },
    t(key, replacements = {}) {
      return translateVisitorLead(key, replacements, this.locale)
    },
    getInitialCameraMode() {
      const userAgent = navigator.userAgent || ''
      const isMobile = /IPHONE|IPAD|IPOD|ANDROID/i.test(userAgent)
      return isMobile ? 'rear' : 'auto'
    },
    getAvailableCameraModes() {
      const initialMode = this.getInitialCameraMode()
      return initialMode === 'auto'
        ? ['auto', 'rear', 'front']
        : ['rear', 'front', 'auto']
    },
    switchCamera() {
      const currentIndex = this.cameraModes.indexOf(this.cameraMode)
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % this.cameraModes.length : 0
      this.loading = true
      this.error = ''
      this.cameraMode = this.cameraModes[nextIndex]
    },
    mapError(error) {
      if (!error) {
        return this.makeMappedError('camera_start_failed', 'The camera could not be started.')
      }
      if (error.name === 'NotAllowedError') {
        return this.makeMappedError('camera_permission_denied', 'Camera access was denied.')
      }
      if (error.name === 'NotFoundError') {
        return this.makeMappedError('camera_not_found', 'No camera was found.')
      }
      if (error.name === 'NotSupportedError') {
        return this.makeMappedError('camera_https_required', 'Camera access is only supported over HTTPS.')
      }
      if (error.name === 'NotReadableError') {
        return this.makeMappedError('camera_in_use', 'The camera is already used by another application.')
      }
      if (error.name === 'OverconstrainedError') {
        return this.makeMappedError('camera_unavailable', 'The requested camera is not available.')
      }
      if (error.name === 'StreamApiNotSupportedError') {
        return this.makeMappedError('camera_api_unsupported', 'Your browser does not support the camera API.')
      }
      const fallbackMessage = error && error.message
        ? `The camera could not be started: ${error.message}`
        : 'The camera could not be started.'
      return this.makeMappedError('camera_start_failed', fallbackMessage)
    },
    makeMappedError(code, fallbackMessage) {
      return {
        code,
        fallbackMessage,
        message: translateVisitorLeadError(code, fallbackMessage, this.locale)
      }
    }
  }
}
</script>

<style scoped>
.visitor-live-scanner {
  position: absolute;
  inset: 0;
}

.visitor-live-scanner :deep(video),
.visitor-live-scanner :deep(canvas) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visitor-live-scanner :deep(.qrcode-stream-wrapper) {
  width: 100%;
  height: 100%;
}

.scanner-loader,
.scanner-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
}

.scanner-loader {
  background: rgba(0, 0, 0, 0.18);
}

.scanner-switch-button {
  position: absolute;
  top: max(1rem, env(safe-area-inset-top));
  right: 1rem;
  z-index: 3;
  border: 0;
  background: rgba(255, 255, 255, 0.9);
  color: #1d395c;
}

.scanner-error {
  align-items: flex-end;
  padding-bottom: 7rem;
  color: #fff;
  font-size: 0.95rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.62), rgba(0, 0, 0, 0));
}
</style>
